"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS, useAuth } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2, Search, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import type { RequestQuoteModalData } from "@/lib/modals/data/modal-data-types";

// Mounted globally via DetailPageModals. Two entry points share this one modal:
//   1) Deal detail / row action → opens with { dealId, dealTitle } in the data
//      payload (deal preselected).
//   2) Nav-bar "Request a Quote" quick action → opens with NO data; the rep
//      picks a deal here first (FTS search), mirroring the Slack-native flow.
// Either way it POSTs to /api/deals/request-quote, which creates the
// technical-team task via the shared createQuoteRequestTask (same path as the
// Slack "Request quote" button). It does NOT read DetailActionContext — that is
// null at the global mount. See GLOBAL_MODAL_NO_ACTION_CONTEXT.md.

export function RequestQuoteModal() {
  return (
    <ModalContainer<RequestQuoteModalData> modalId={MODAL_IDS.deals.requestQuote}>
      {({ isOpen, data, onClose }) => (
        <RequestQuoteModalContent isOpen={isOpen} data={data} onClose={onClose} />
      )}
    </ModalContainer>
  );
}

interface DealHit {
  id: string;
  title: string;
  companyName: string | null;
}

interface RequestQuoteModalContentProps {
  isOpen: boolean;
  data?: RequestQuoteModalData;
  onClose: () => void;
}

function RequestQuoteModalContent({
  isOpen,
  data,
  onClose,
}: RequestQuoteModalContentProps) {
  const { getAuthToken } = useAuth();
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Deal chosen via the in-modal picker (nav-bar entry, no preselected deal).
  const [pickedDeal, setPickedDeal] = useState<DealHit | null>(null);

  // Reset transient state each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setNotes("");
      setPickedDeal(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const effectiveDealId = data?.dealId ?? pickedDeal?.id ?? null;
  const effectiveDealTitle = data?.dealTitle ?? pickedDeal?.title ?? null;

  const handleSubmit = useCallback(async () => {
    if (!effectiveDealId) return;
    setIsSubmitting(true);
    try {
      const response = await fetch("/api/deals/request-quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dealId: effectiveDealId,
          specialNotes: notes.trim() || undefined,
        }),
      });
      const payload: { ok?: boolean; taskUrl?: string; error?: string } =
        await response.json().catch(() => ({}));

      if (!response.ok || !payload.ok) {
        toast.error(payload.error || "Couldn't send the quote request.");
        return;
      }

      toast.success("Quote requested from the technical team — due in 2 days.", {
        action: payload.taskUrl
          ? { label: "View task", onClick: () => window.open(payload.taskUrl, "_blank") }
          : undefined,
      });
      setNotes("");
      setPickedDeal(null);
      onClose();
    } catch {
      toast.error("Couldn't send the quote request. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [effectiveDealId, notes, onClose]);

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.deals.requestQuote}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Request quote from technical team"
      description={
        effectiveDealTitle
          ? `Sends a quote request for "${effectiveDealTitle}" as a task due in 2 days.`
          : "Pick the deal you need quoted — the request goes to the technical team as a task due in 2 days."
      }
      maxWidth="md"
    >
      <div className="space-y-4 p-4">
        {!effectiveDealId ? (
          <DealPicker getAuthToken={getAuthToken} onPick={setPickedDeal} />
        ) : (
          <>
            {/* Show which deal when picked here (deal-detail entry already names it in the description). */}
            {pickedDeal && (
              <div className="flex items-center justify-between gap-2 rounded-lg border bg-muted/40 p-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{pickedDeal.title}</div>
                  {pickedDeal.companyName && (
                    <div className="truncate text-xs text-muted-foreground">
                      {pickedDeal.companyName}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPickedDeal(null)}
                  disabled={isSubmitting}
                >
                  Change
                </Button>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              All context — revenue, cost, recurring, probability, description, and
              internal notes — is pulled from the deal. To change any of it, edit the
              deal directly. Add anything else the technical team should know below.
            </p>

            <div className="space-y-1.5">
              <label htmlFor="request-quote-notes" className="text-sm font-medium">
                Notes for the technical team (optional)
              </label>
              <Textarea
                id="request-quote-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything special about this deal the technical team should know when quoting…"
                rows={5}
                maxLength={4000}
                disabled={isSubmitting}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Sending…" : "Send request"}
              </Button>
            </div>
          </>
        )}
      </div>
    </ResponsiveModal>
  );
}

/** FTS deal search used when the modal is opened without a preselected deal. */
function DealPicker({
  getAuthToken,
  onPick,
}: {
  getAuthToken: () => string | null;
  onPick: (deal: DealHit) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DealHit[]>([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const token = getAuthToken();
        const res = await fetch("/api/search/deals", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ query: q, limit: 20, offset: 0 }),
        });
        const json: { data?: Array<{ id: string; title?: string; company_name?: string }> } =
          await res.json().catch(() => ({}));
        setResults(
          (json.data ?? []).map((d) => ({
            id: d.id,
            title: d.title ?? "Untitled deal",
            companyName: d.company_name ?? null,
          })),
        );
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query, getAuthToken]);

  return (
    <div className="space-y-2">
      <label htmlFor="request-quote-deal-search" className="text-sm font-medium">
        Which deal needs a quote?
      </label>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id="request-quote-deal-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search deals by title or company…"
          className="pl-8"
          autoFocus
        />
      </div>
      <div className="max-h-64 overflow-y-auto rounded-lg border">
        {searching && (
          <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Searching…
          </div>
        )}
        {!searching && query.trim().length >= 2 && results.length === 0 && (
          <div className="px-3 py-3 text-sm text-muted-foreground">No deals found.</div>
        )}
        {!searching && query.trim().length < 2 && (
          <div className="px-3 py-3 text-sm text-muted-foreground">
            Type at least 2 characters to search.
          </div>
        )}
        {results.map((deal) => (
          <button
            key={deal.id}
            type="button"
            onClick={() => onPick(deal)}
            className="flex w-full items-center gap-2 border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted/50"
          >
            <TrendingUp className="h-4 w-4 shrink-0 text-purple-500" />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{deal.title}</span>
              {deal.companyName && (
                <span className="block truncate text-xs text-muted-foreground">
                  {deal.companyName}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
